import { readCurrent, readOptionsService, updateCurrent } from '@/redux/actions';
import * as Constants from '@/redux/constants';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { Alert, Stack, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import { useDialogs } from '@toolpad/core';
import { Button } from 'components/button';
import { PasswordInput, passwordStrengthCriteria, TextInput } from 'components/input';
import { useDispatch, useSelector } from 'hooks';
import { convertEmptyStringToNull } from 'lib/utils';
import { useI18n } from 'locales';
import get from 'lodash/get';
import { Giver } from 'models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { shallowEqual } from 'react-redux';
import ServiceSelectBox from '../../register/components/ServiceSelectBox';
import PortalHeader from '../components/PortalHeader';

const selector = (state) => ({
  currentInfo: state.currentInfo,
  optionsServiceInfo: state.optionsServiceInfo,
});

function ProfilePage() {
  const dispatch = useDispatch();
  const t = useI18n();
  const { currentInfo, optionsServiceInfo } = useSelector(selector, shallowEqual);
  const { control, formState: { errors, isDirty }, handleSubmit, watch, setError, setValue, reset } = useForm();
  const dialogs = useDialogs();
  const isFetching = currentInfo.isFetching;
  const serviceOptions = get(optionsServiceInfo, 'data.services', []).map(service => ({
    ...service,
    label: service.label === 'Others' ? t('Others') : service.label
  }));
  const watchedServiceId = watch('serviceId');

  const fields = useMemo(() => {
    return [
      { name: 'firstNameEng', label: t('First Name (English) *'), type: 'text' },
      { name: 'lastNameEng', label: t('Last Name (English) *'), type: 'text' },
      { name: 'firstNameChi', label: t('First Name (Chinese)'), type: 'text' },
      { name: 'lastNameChi', label: t('Last Name (Chinese)'), type: 'text' },
      { name: 'receiptName', label: t('Name on Tax Receipt'), type: 'text', helperText: t('Used for issuing tax receipt. Default to First Name and Last Name'), readOnly: true },
      { name: 'email', label: t('Email'), type: 'email', readOnly: true },
      { name: 'phone', label: t('Phone *'), type: 'tel', maxLength: 15, },
      // { name: 'countryCode', label: 'Country Code', type: 'text', helperText: '(Optional)' },
      // { name: 'address', label: 'Address', type: 'text', helperText: '(Optional)' },
      // { name: 'city', label: 'City', type: 'text', helperText: '(Optional)' },
      // { name: 'country', label: 'Country', type: 'text', helperText: '(Optional)' },
    ];
  }, [t]);

  const passwordFields = useMemo(() => {
    return [
      { name: 'currentPassword', label: t('Current Password'), type: 'password' },
      { name: 'newPassword', label: t('New Password'), type: 'password' },
      { name: 'confirmPassword', label: t('Confirm New Password'), type: 'password' },
    ];
  }, [t]);

  useEffect(() => {
    dispatch(readCurrent());
    dispatch(readOptionsService());
  }, [dispatch]);

  useEffect(() => {
    if (currentInfo.dataFetched && currentInfo.data) {
      const data = currentInfo.data;
      reset({
        firstNameEng: data.firstNameEng || '',
        lastNameEng: data.lastNameEng || '',
        firstNameChi: data.firstNameChi || '',
        lastNameChi: data.lastNameChi || '',
        receiptName: data.receiptName || '',
        email: data.email || '',
        phone: data.phone || '',
        countryCode: data.countryCode || '',
        address: data.address || '',
        city: data.city || '',
        country: data.country || '',
        serviceId: data.serviceId || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    }
  }, [currentInfo.dataFetched, currentInfo.data, reset]);

  useEffect(() => {
    switch (currentInfo.type) {
      case Constants.UPDATE_CURRENT.SUCCESS:
        dialogs.alert(t('Your profile has been updated successfully.'), {
          title: t('Success'),
          okText: t('OK'),
        });
        // Clear password fields
        setValue('currentPassword', '');
        setValue('newPassword', '');
        setValue('confirmPassword', '');
        // Refresh profile data
        dispatch(readCurrent());
        break;
      case Constants.UPDATE_CURRENT.FAILURE: {
        const errors = get(currentInfo, 'error.errors', {});
        for (const entry of Object.entries(errors)) {
          if (Array.isArray(entry[1])) {
            for (const message of entry[1]) {
              setError(entry[0], { type: 'manual', message });
            }
          } else {
            setError(entry[0], { type: 'manual', message: entry[1] });
          }
        }
        break;
      }
    }
  }, [currentInfo, dialogs, setError, setValue, dispatch]);

  const onSubmit = useCallback((data) => {
    data = convertEmptyStringToNull(data);

    // Only include password fields if newPassword is provided
    if (!data.newPassword) {
      delete data.currentPassword;
      delete data.newPassword;
      delete data.confirmPassword;
    } else if (!passwordStrengthCriteria.every((test) => test(data.newPassword || ''))) {
      dialogs.alert(t('New password does not meet strength requirements. Please check the checklist and try again.'), {
        title: t('Weak Password'),
        okText: t('OK'),
      });
      return;
    }

    dispatch(updateCurrent({ payload: data }));
  }, [dispatch, dialogs, t]);

  const onError = useCallback((/* errors */) => {
    dialogs.alert(t('There are errors in the form. Please check the form and try again.'), { title: t('Error'), okText: t('OK') });
  }, [dialogs, t]);

  const [isMobile, setIsMobile] = useState(false); // eslint-disable-line no-unused-vars

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit, onError)}
      gap={4}
    >
      <PortalHeader header={t('MY PROFILE')} />

      <Stack gap={2} sx={{ px: 2 }}>
        {currentInfo.error && currentInfo.error.message && (
          <Alert severity="error">
            {currentInfo.error.message}
          </Alert>
        )}

        <Stack gap={1}>
          <Typography variant='h5' color='secondary.main' sx={{ fontWeight: 'bold' }}>
            {t('Personal Information')}
          </Typography>

          <Stack gap={2}>
            {fields.map((field) => {
              if (field.name === 'address') {
                return (
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    rules={Giver.validationRules[field.name]}
                    render={({ field: { onChange, value } }) => (
                      <TextInput
                        label={field.label}
                        type={field.type}
                        value={value || ''}
                        onChange={onChange}
                        error={!!errors[field.name]}
                        helper={errors[field.name]?.message || field.helperText}
                        fullWidth
                        multiline
                        rows={3}
                        readOnly={field.readOnly}
                        maxLength={field.maxLength}
                      />
                    )}
                  />
                );
              }

              return (
                <Controller
                  key={field.name}
                  name={field.name}
                  control={control}
                  rules={Giver.validationRules[field.name]}
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      label={field.label}
                      type={field.type}
                      value={value || ''}
                      onChange={onChange}
                      error={!!errors[field.name]}
                      helper={errors[field.name]?.message || field.helperText}
                      fullWidth
                      readOnly={field.readOnly}
                      maxLength={field.maxLength}
                    />
                  )}
                />
              );
            })}
          </Stack>
        </Stack>

        <Stack gap={1}>
          <Typography variant='h5' color='secondary.main' sx={{ fontWeight: 'bold' }}>
            {t('Which Is Your Regular Service?')}
          </Typography>

          <Controller
            name="serviceId"
            control={control}
            rules={Giver.validationRules.serviceId}
            render={({ field: { onChange } }) => (
              <FormControl
                variant='filled'
                error={!!errors.serviceId}
                fullWidth
              >
                <Grid container gap={1} direction="row">
                  {
                    serviceOptions?.map((service, index) => (
                      <ServiceSelectBox
                        key={`service-select-box-${index}`}
                        service={service}
                        selected={service.value === watchedServiceId}
                        onSelect={() => onChange(service.value)}
                      />
                    ))
                  }
                </Grid>
                <FormHelperText sx={{ pt: 1 }}>{get(errors, 'serviceId.message')}</FormHelperText>
              </FormControl>
            )}
          />
        </Stack>

        <Stack gap={1}>
          <Typography variant='h5' color='secondary.main' sx={{ fontWeight: 'bold' }}>
            {t('Change Password (Optional)')}
          </Typography>
          <Alert severity="info">
            {t('Leave password fields blank if you don\'t want to change your password.')}
          </Alert>

          <Stack gap={2}>
            {passwordFields.map((field) => {
              return (
                <Controller
                  key={field.name}
                  name={field.name}
                  control={control}
                  rules={watch('newPassword') ? Giver.validationRules[field.name] : {}}
                  render={({ field: { onChange, value } }) => (
                    <PasswordInput
                      label={field.label}
                      value={value || ''}
                      onChange={onChange}
                      error={!!errors[field.name]}
                      helper={errors[field.name]?.message}
                      showChecklist={field.name === 'newPassword'}
                      maxLength={field.maxLength}
                      fullWidth
                    />
                  )}
                />
              );
            })}
          </Stack>
        </Stack>

        <Stack sx={{ display: 'flex', width: '300px', alignSelf: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            leading={faSave}
            fullWidth
            disabled={isFetching || !isDirty}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              mb: 4,
            }}
          >
            {isFetching ? t('Saving...') : t('Save Profile')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default ProfilePage;
